import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiRoles } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const EMPTY = { name: '', defaultHourlyRate: '' };

function formatRate(rate) {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = typeof rate === 'string' ? Number(rate) : rate;
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2)} €/h`;
}

export default function RolesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiRoles.list(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      defaultHourlyRate: r.defaultHourlyRate ?? '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const rateStr = String(form.defaultHourlyRate ?? '').trim();
      const payload = {
        name: form.name.trim(),
        defaultHourlyRate: rateStr === '' ? null : rateStr,
      };
      if (editing) {
        await apiRoles.update(editing.id, payload);
      } else {
        await apiRoles.create(payload);
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (r) => {
    if (!window.confirm(`Archiver le rôle "${r.name}" ?`)) return;
    await apiRoles.archive(r.id);
    await reload();
  };

  const onRestore = async (r) => {
    await apiRoles.restore(r.id);
    await reload();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Rôles</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouveau rôle
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Taux par défaut</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Personnes</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun rôle.</td>
              </tr>
            ) : (
              items.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                    {r.name}
                    {r.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivé)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {formatRate(r.defaultHourlyRate)}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {r._count?.people ?? 0}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(r.archivedAt)}
                      onEdit={() => openEdit(r)}
                      onArchive={() => onArchive(r)}
                      onRestore={() => onRestore(r)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifier le rôle' : 'Nouveau rôle'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="role-form"
              disabled={submitting || !form.name.trim()}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="role-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>Nom</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={128}
              autoFocus
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Taux horaire par défaut (€/h, optionnel)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.defaultHourlyRate}
              onChange={(e) => setForm({ ...form, defaultHourlyRate: e.target.value })}
              placeholder="ex. 85.00"
              style={inputStyle}
            />
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>
    </div>
  );
}
